import {
    SchemaFormFields,
    SteppedDialogFormProps,
    useSteppedDialogForm,
    useSteppedDialogFormControls,
} from '@mews-commander/form';
import { createZodFormResolver, LocalizedText, lt, showSuccessAlert, useFeatureFlag } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { createOption, EnterpriseLink } from '@mews-commander/ui';
import { useGetExportTargets, useScheduleExportPayoutReport } from '../../queries';
import {
    CombinedSchemaType,
    defaultValues,
    exportConfigFormDefinition,
    exportConfigSchema,
    getScheduleConfigFormDefinition,
    scheduleConfigSchema,
} from './schema';

type ScheduleExportSteps = 'exportConfig' | 'scheduleConfig';

export const useCreateScheduledExportDialogForm = () =>
    useSteppedDialogForm<ScheduleExportSteps>({
        id: 'createScheduledExportForm',
        title: lt('ScheduleRecurrentExport'),
        steps: ['exportConfig', 'scheduleConfig'],
        stepLabels: {
            exportConfig: lt('ExportConfiguration'),
            scheduleConfig: lt('Schedule'),
        },
        renderForm: (props) => <CreateScheduledExportForm {...props} />,
        saveLabel: lt('Export'),
    });

const CreateScheduledExportForm = (props: SteppedDialogFormProps<ScheduleExportSteps>) => {
    const enablePayoutDetailsInPayoutExport: boolean = useFeatureFlag(FeatureFlag.enablePayoutDetailsInPayoutExport);
    const { data: exportTargets } = useGetExportTargets();

    const scheduleConfigFormDefinition = getScheduleConfigFormDefinition(
        exportTargets?.map((target) =>
            createOption({
                value: target.id,
                label: target.name,
            })
        ) ?? []
    );

    const { mutateAsync } = useScheduleExportPayoutReport();

    const { renderFormContent } = useSteppedDialogFormControls<ScheduleExportSteps, CombinedSchemaType>({
        ...props,
        stepResolvers: {
            exportConfig: createZodFormResolver(exportConfigSchema),
            scheduleConfig: createZodFormResolver(scheduleConfigSchema),
        },
        onSubmit: async (formValues) => {
            await mutateAsync(formValues, {
                onSuccess: () => {
                    showSuccessAlert({
                        title: lt('ExportScheduled'),
                        action: {
                            text: (
                                <EnterpriseLink to="Export/Index" search={{ tab: 'scheduled' }} inherit>
                                    <LocalizedText textKey={TextKey.GoToScheduledExports} />
                                </EnterpriseLink>
                            ),
                            onClick: () => {},
                        },
                    });
                },
            });
        },
        defaultValues,
        mode: 'onBlur',
    });

    return renderFormContent((step) => {
        switch (step) {
            case 'exportConfig':
                return (
                    <SchemaFormFields
                        schema={exportConfigSchema}
                        formDefinition={{
                            ...exportConfigFormDefinition,
                            renderFields: enablePayoutDetailsInPayoutExport
                                ? undefined
                                : ['name', 'reportInterval', 'format'],
                        }}
                    />
                );
            case 'scheduleConfig':
                return <SchemaFormFields schema={scheduleConfigSchema} formDefinition={scheduleConfigFormDefinition} />;
        }
    });
};
